<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { deleteCommissionAction, getCommissionsAction } from '@/api/functions/settings/get-commissions-action'
import { useMutationApi, useQueryApi } from '@/composables/useApi'
import { useGeneralSettings } from '@/composables/useGeneralSettings'
import { useToast } from '@/composables/useToast'
import { settingsTabs } from '@/constants/setting-tabs.ts'
import { getErrorMessage } from '@/utils/errorUtils'
import SettingsOverview from '../partials/SettingsOverview.vue'
import CreateCommissionModal from './partials/CreateCommissionModal.vue'
import UpdateCommissionModal from './partials/UpdateCommissionModal.vue'

const router = useRouter()
const route = useRoute()
const toast = useToast()

const showDeleteModal = ref(false)
const showUpdateModal = ref(false)
const showCreateModal = ref(false)
const currentPage = ref(1)
const activeFilters = ref<Record<string, any>>({})

const { currencies } = useGeneralSettings()

// --- Query Commissions ---
const { data: commissionsData, refetch } = useQueryApi({
  queryKey: computed(() => ['commissions', currentPage.value, activeFilters.value]),
  queryFn: () => {
    const filters = Object.fromEntries(
      Object.entries(activeFilters.value).filter(([key, value]) => key !== 'page' && value !== false)
    )

    return getCommissionsAction({
      page: currentPage.value,
      ...filters,
    })
  },
  // keepPreviousData: true,

  staleTime: 1000 * 60 * 3,
})

// --- Computed rows ---
const commissions = computed(() => {
  return (
    commissionsData.value?.data?.map((item, idx) => ({
      ...item,
      index: idx + 1,
      currencyName: (item.currency as unknown as { titleFa?: string })?.titleFa ?? '-',
      feeType: item.type === 'FIXED' ? 'مقدار در واحد' : 'درصد',
      minAmount: item.from ?? 0,
      maxAmount: item.till ?? 0,
      feeAmount: item.amount ?? '-',
    })) || []
  )
})

// --- Pagination ---
const pagination = computed(() => commissionsData.value?.pagination)
const totalItems = computed(() => pagination.value?.total ?? 0)
const itemsPerPage = computed(() => pagination.value?.perPage ?? 10)

// --- Table Columns ---
const columns = [
  { key: 'currencyName', label: 'ارز', class: 'col-span-3 text-sm' },
  { key: 'feeType', label: 'نوع کارمزد', class: 'col-span-2 text-sm' },
  { key: 'minAmount', label: 'از مبلغ', class: 'col-span-1 font-fa text-sm', type: 'number' },
  { key: 'maxAmount', label: 'تا مبلغ', class: 'col-span-1 font-fa text-sm', type: 'number' },
  { key: 'feeAmount', label: 'مقدار کارمزد', class: 'col-span-3 font-fa text-sm' },
]

// --- Filter Fields ---
const filterFields = computed(() => [
  {
    type: 'select',
    key: 'currency',
    title: 'جستجوی ارز',
    options: currencies.value,
  },
])

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const onApplyFilters = (values: Record<string, any>) => {
  activeFilters.value = { ...values }
  currentPage.value = 1
  const query = Object.fromEntries(Object.entries(values).filter(([_, v]) => v))
  router.push({ path: route.path, query })
  refetch()
}

const onResetFilters = () => {
  activeFilters.value = {}
  currentPage.value = 1
  router.push({ path: route.path, query: {} })
  refetch()
}

// --- Actions ---
const actions = [
  { label: 'ویرایش', handlerName: 'editCommission' },
  { label: 'حذف', handlerName: 'deleteCommission' },
]

const selectedCommission = ref()

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const onEditCommission = (commission: any) => {
  selectedCommission.value = commission
  showUpdateModal.value = true
}

const deleteCommissionMutation = useMutationApi({
  mutationFn: (id: string | number) => deleteCommissionAction(id),
  onSuccess: () => {
    toast.success('کارمزد با موفقیت حذف شد')
    refetch()
  },
  onError: (error: Error) => {
    toast.error(getErrorMessage(error), 'خطا')
  },
})

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const onDeleteCommission = (commission: any) => {
  selectedCommission.value = commission
  showDeleteModal.value = true
}

const handleDeleteCommission = async () => {
  if (!selectedCommission.value?.id) return
  deleteCommissionMutation.mutate(selectedCommission.value.id)
}

// --- Pagination handler ---
const goToPage = (page: number) => {
  currentPage.value = page
  refetch()
}

// --- Mounted: initialize filters from query ---
onMounted(() => {
  if (Object.keys(route.query).length > 0) {
    const { page, ...filters } = route.query

    activeFilters.value = filters

    if (page) {
      currentPage.value = Number(page)
    }
  }
})
</script>

<template>
  <div class="w-full mb-12">

    <ConfirmModal v-model="showDeleteModal" title="حذف کارمزد" :message="'آیا از حذف این کارمزد اطمینان دارید؟'"
      confirm-text="بله حذف شود" cancel-text="بازگشت" @confirm="handleDeleteCommission"
      @cancel="showDeleteModal = false" />

    <UpdateCommissionModal v-model="showUpdateModal" :currencies="currencies" :selectedCommission="selectedCommission"
      @confirm="refetch" @cancel="showUpdateModal = false" />

    <CreateCommissionModal v-model="showCreateModal" :currencies="currencies" @confirm="refetch"
      @cancel="showCreateModal = false" />

    <SettingsOverview :tabs="settingsTabs">
      <template #actions>
        <div class="flex items-center gap-2 pb-1">
          <Button :isAdmin="true" variant="outline" @click="showCreateModal = true"
            class="px-3! rounded-lg! h-8! border-2 py-0 text-sm font-medium">
            ایجاد کارمزد
          </Button>
        </div>
      </template>
    </SettingsOverview>

    <!-- Filters Section -->
    <Filters :fields="filterFields" @apply="onApplyFilters" @reset="onResetFilters" class="mt-20 lg:mt-0" />

    <div class="flex flex-col gap-2 mt-3">
      <Table :columns="columns" :rows="commissions" :actions="actions" @primaryAction="onEditCommission"
        @secondaryAction="onDeleteCommission" />

      <!-- Pagination -->
      <Pagination v-if="totalItems > 0" v-model:currentPage="currentPage" :itemsPerPage="itemsPerPage"
        :totalItems="totalItems" @update:currentPage="goToPage" :isAdmin="true" />
    </div>
  </div>
</template>
